/**
 * Packages.icons.js
 *
 * Small hand-authored stroke-icon set for the redesigned "Our Program"
 * page. Kept local (not pulled from an icon library) so this page doesn't
 * pick up a new dependency for a dozen glyphs. All 24x24, stroke-based,
 * `currentColor` so each consumer sets color via CSS rather than a prop.
 * Every icon here is paired with visible text, so each is decorative
 * (aria-hidden) rather than needing its own accessible name.
 */

import React from "react";

const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
  focusable: "false",
};

// ── Hero trust badges ───────────────────────────────────────────────────────

export const ShieldIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M12 3.5l7 3v5.2c0 4.6-3 7.9-7 8.8-4-.9-7-4.2-7-8.8V6.5l7-3Z" />
    <path d="M8.75 12.25l2.25 2.25 4.25-4.5" />
  </svg>
);

export const CalendarCheckIcon = (props) => (
  <svg {...base} {...props}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 9.5h17M8 3v3M16 3v3" />
    <path d="M9 14l2 2 4-4.2" />
  </svg>
);

export const HeartPulseIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M12 19.5s-7.2-4.4-7.2-9.6a4.3 4.3 0 017.2-3.1 4.3 4.3 0 017.2 3.1c0 5.2-7.2 9.6-7.2 9.6Z" />
    <path d="M7 12h2l1.3-2.3L11.7 14 13 11.7l1 1.3h2" />
  </svg>
);

export const TrophyIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M7 4h10v5a5 5 0 01-10 0V4Z" />
    <path d="M7 5.5H4.5a2.5 2.5 0 002.5 2.5M17 5.5h2.5a2.5 2.5 0 01-2.5 2.5" />
    <path d="M12 14v3.5M9 20.5h6M9.5 17.5h5v3h-5z" />
  </svg>
);

// ── Program tabs ────────────────────────────────────────────────────────────

export const SeedIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M12 21c-4-1-6.5-4.3-6.5-8.3C5.5 8 8 5 12 3c4 2 6.5 5 6.5 9.7 0 4-2.5 7.3-6.5 8.3Z" />
    <path d="M12 21V10" />
  </svg>
);

export const LotusIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M12 4c1 3 1 6.5 0 9-1-2.5-1-6 0-9Z" />
    <path d="M6 8c2 2 3.7 4.3 4.2 6.6-3-.3-5.4-2.5-6.4-4.8Z" />
    <path d="M18 8c-2 2-3.7 4.3-4.2 6.6 3-.3 5.4-2.5 6.4-4.8Z" />
    <path d="M5 15.5c1.6 2.2 4 3.3 7 3.3s5.4-1.1 7-3.3c-2.2-.9-4.6-1.4-7-1.4s-4.8.5-7 1.4Z" />
  </svg>
);

export const SproutHeartIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M12 21v-7" />
    <path d="M12 14c-3.5 0-6-2-6-5.3C7.7 9 10 9.4 12 11c2-1.6 4.3-2 6-.3 0 3.3-2.5 5.3-6 5.3Z" />
  </svg>
);

export const FlameIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M12 21c4 0 6.5-2.7 6.5-6.3 0-3-1.8-4.9-3-6.8-.2 2-1 3-2 3.3.5-2.7-.3-5-3-7-1 3-3 4.6-4.5 6.6C4.7 12.3 4 13.8 4 15c0 3.6 4 6 8 6Z" />
  </svg>
);

// ── "Why our programs work" grid ────────────────────────────────────────────

export const ScrollIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M6 4h10a2.5 2.5 0 012.5 2.5v11A2.5 2.5 0 0116 20H8" />
    <path d="M6 4a2.5 2.5 0 00-2.5 2.5V17A2.5 2.5 0 006 19.5" />
    <path d="M8 8.5h7M8 12h7" />
  </svg>
);

export const FlaskIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M10 3.5h4M10.5 4v5.3L6 16.7A2 2 0 007.7 20h8.6a2 2 0 001.7-3.3L13.5 9.3V4" />
    <path d="M8.8 14.5h6.4" />
  </svg>
);

export const PersonUpIcon = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="6" r="2.6" />
    <path d="M6 20c1.2-4 3.2-6.3 6-6.3s4.8 2.3 6 6.3" />
    <path d="M12 13.7V10" />
  </svg>
);

export const ChartUpIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M4 19.5h16" />
    <path d="M6 16.5v-4M11 16.5v-7M16 16.5v-3M18.5 16.5V7" />
    <path d="M14.5 5.5H19v4.5" />
    <path d="M19 5.5L13 11.5 9.7 8.2 5 12.9" />
  </svg>
);

// ── Consultation banner ─────────────────────────────────────────────────────

export const HeadsetIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M4 13v-1a8 8 0 0116 0v1" />
    <rect x="3" y="13" width="4" height="6" rx="1.5" />
    <rect x="17" y="13" width="4" height="6" rx="1.5" />
    <path d="M20 19v.5a3 3 0 01-3 3h-3" />
  </svg>
);

// ── Shared ───────────────────────────────────────────────────────────────────

export const ArrowRightIcon = (props) => (
  <svg {...base} {...props} strokeWidth={2.3}>
    <path d="M4 12h16M13 5l7 7-7 7" />
  </svg>
);

export const CheckIcon = (props) => (
  <svg {...base} {...props} strokeWidth={2.4} viewBox="0 0 20 20">
    <polyline points="4 10 8 14 16 6" />
  </svg>
);

export const StarIcon = (props) => (
  <svg {...base} {...props} fill="currentColor" stroke="none">
    <path d="M12 2.5l2.7 5.9 6.3.7-4.7 4.4 1.3 6.3L12 16.8 6.4 19.8l1.3-6.3-4.7-4.4 6.3-.7L12 2.5Z" />
  </svg>
);
